import React, { useEffect } from "react";
import { Box, Flex, Grid, Text } from "theme-ui";
import srsLogo from "./images/srs_logo.png";
import srsLogoWhite from "./images/srs_logo_white.png";
import roofHubLogo from "./images/goodleapplus-hub-logo.png";
import roofHubLogoWhite from "./images/goodleapplus-hub-logo-white.png";
import glPlusGrey from "./images/GLplus_grey_gradient.svg";
import goodLeapLogoWhite from "./images/good-white-gradient-cR.svg";
import goodLeapLogoOnDark from "../../images/goodleap-white-orange.svg";
// import poweredByGoodLeapMaroon from "../../images/roofhub-powered-by-goodleap-maroon.svg";
// import roofHubInfographic from "./images/roofhub_infographic.svg";
import { GoodLeapPlusPartnerFlowStrip } from "./GoodLeapPlusPartnerFlowStrip";

/** RoofHub — Brand Guidelines Rev Oct 2025 (web) */
const rh = {
  maroon: "#762123",
  gold: "#f2b533",
  black: "#000000",
};

/** GoodLeap — aligned with goodleap.com / in-app surfaces (#003439, teal family, orange accent) */
const gl = {
  teal: "#003439",
  tealMid: "#007680",
  tealBright: "#00838f",
  orange: "#FF6B35",
  wash: "#e8f4f3",
  washMuted: "#f0f7f6",
};

const text = {
  primary: "#1a1a1a",
  muted: "#475569",
  subtle: "#64748b",
};

const border = {
  tealSoft: "rgba(0, 52, 57, 0.14)",
  maroonSoft: "rgba(118, 33, 35, 0.22)",
};

const LATO_LINK_ID = "roofhub-brand-lato-font";

function useRoofHubBrandFont() {
  useEffect(() => {
    if (document.getElementById(LATO_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = LATO_LINK_ID;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,400;0,700;0,900;1,400&display=swap";
    document.head.appendChild(link);
  }, []);
}

/** Tighter reading column — centered */
const section = {
  maxWidth: "880px",
  mx: "auto",
  px: [2, 3],
};

/** Shared cap height so SRS wordmark and GoodLeap plate feel balanced (header — keep prominent) */
const TOP_LOGO_BAND_PX = 72;

const logoImgTopSrs = {
  display: "block",
  objectFit: "contain",
  height: TOP_LOGO_BAND_PX,
  width: "auto",
  maxWidth: 380,
};

const logoImgTopGl = {
  display: "block",
  objectFit: "contain",
  height: Math.round(TOP_LOGO_BAND_PX * 0.78),
  width: "auto",
  maxWidth: 290,
};

/** Footer lockup — same bounding box per column; each mark scales inside with `object-fit: contain` */
const FOOTER_LOGO_BOX = {
  width: "100%",
  maxWidth: "200px",
  height: "44px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mx: "auto",
};

const logoImgFooterLockup = {
  display: "block",
  margin: "auto",
  maxWidth: "100%",
  maxHeight: "100%",
  width: "auto",
  height: "auto",
  objectFit: "contain",
};

const FOOTER_CELL_MIN_H = 52;

const RoofHub = () => {
  useRoofHubBrandFont();

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        color: text.primary,
        minHeight: "100vh",
        fontFamily: "'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif",
      }}
    >
      {/* Top partnership strip — GoodLeap primary teal */}
      <Box
        sx={{
          textAlign: "center",
          py: "10px",
          backgroundColor: gl.teal,
          borderBottom: `3px solid ${rh.gold}`,
        }}
      >
        <Text
          sx={{
            fontSize: "11px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#fff",
            fontWeight: 700,
          }}
        >
          Built for the SRS + GoodLeap partnership
        </Text>
      </Box>

      {/* Logo bar */}
      <Flex
        sx={{
          maxWidth: "840px",
          width: "100%",
          mx: "auto",
          px: section.px,
          py: [3, 4],
          alignItems: "center",
          justifyContent: "center",
          gap: [4, 5],
          flexWrap: "wrap",
        }}
      >
        <img src={srsLogo} alt="SRS Distribution" style={logoImgTopSrs} />
        <Box
          sx={{
            width: "1px",
            height: TOP_LOGO_BAND_PX,
            backgroundColor: border.tealSoft,
            display: ["none", "block"],
            flexShrink: 0,
            alignSelf: "center",
          }}
        />
        <TealBrandPlate
          sx={{
            px: [3, 4],
            py: "9px",
            flexShrink: 0,
            minHeight: TOP_LOGO_BAND_PX,
            boxSizing: "border-box",
            alignItems: "center",
          }}
        >
          <img src={goodLeapLogoOnDark} alt="GoodLeap" style={logoImgTopGl} />
        </TealBrandPlate>
      </Flex>

      {/* Hero */}
      <Box
        sx={{
          ...section,
          mb: [3, 4],
          p: [3, 3],
          border: `2px solid ${border.maroonSoft}`,
          borderRadius: "8px",
          background: `linear-gradient(165deg, ${gl.wash} 0%, #fff 42%, ${gl.washMuted} 100%)`,
          boxShadow: `0 0 0 1px ${border.tealSoft}`,
        }}
      >
        <Grid
          columns={[1, 1, "1fr 1.05fr 0.95fr"]}
          gap={[3, 3]}
          sx={{ alignItems: "start" }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={glPlusGrey}
                alt="GoodLeap Plus"
                style={{
                  width: "100%",
                  maxWidth: 300,
                  height: "auto",
                  display: "block",
                  margin: "0 auto",
                }}
              />
            </Box>
            <Text
              as="span"
              sx={{
                mt: 2,
                fontSize: ["1.1rem", "1.25rem"],
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "0.05em",
                backgroundColor: gl.teal,
                px: "10px",
                py: "4px",
                borderRadius: "4px",
                display: "inline-block",
                lineHeight: 1.35,
              }}
            >
              Financing
            </Text>
            <Box
              sx={{
                mt: "10px",
                width: "100%",
                maxWidth: "320px",
                mx: "auto",
                px: [2, 3],
                boxSizing: "border-box",
              }}
            >
              <Text
                sx={{
                  display: "block",
                  fontSize: ["0.92rem", "1rem"],
                  fontWeight: 700,
                  fontStyle: "italic",
                  letterSpacing: "0.1em",
                  color: rh.maroon,
                  textAlign: "left",
                  lineHeight: 1.3,
                }}
              >
                More approvals.
              </Text>
              <Text
                sx={{
                  display: "block",
                  mt: "4px",
                  fontSize: ["0.92rem", "1rem"],
                  fontWeight: 700,
                  fontStyle: "italic",
                  letterSpacing: "0.1em",
                  color: rh.maroon,
                  textAlign: "right",
                  lineHeight: 1.3,
                }}
              >
                Stronger together.
              </Text>
            </Box>
            <Box
              sx={{
                width: "100%",
                maxWidth: "240px",
                mx: "auto",
                mt: 2,
                boxSizing: "border-box",
                background: `linear-gradient(165deg, #5a2840 0%, ${rh.maroon} 45%, #5c1a1c 100%)`,
                borderRadius: "8px",
                py: [2, "10px"],
                px: [2, 3],
                borderTop: `2px solid ${rh.gold}`,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Flex
                sx={{
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: "6px",
                }}
              >
                <Text
                  sx={{
                    fontSize: "0.52rem",
                    letterSpacing: "0.2em",
                    color: "rgba(255,255,255,0.78)",
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  POWERED BY
                </Text>
                <Box
                  sx={{
                    height: "32px",
                    width: "100%",
                    maxWidth: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={goodLeapLogoOnDark}
                    alt="GoodLeap"
                    style={{
                      display: "block",
                      maxHeight: "100%",
                      maxWidth: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Flex>
            </Box>
          </Box>
          <Box>
            <Box as="h2" sx={{ fontSize: ["1.2rem", "1.35rem"], fontWeight: 800, mb: 2, color: gl.teal, lineHeight: 1.25 }}>
              Look for the GoodLeap Plus logo
            </Box>
            <Text sx={{ fontSize: "0.95rem", lineHeight: 1.55, color: text.muted }}>
              Your signal that financing is just one click away. Share one link across your website,
              SMS, email, and shareable links—customers apply in minutes, and everything flows into
              RoofHub.
            </Text>
          </Box>
          <Grid columns={1} gap={2}>
            {[
              { icon: "⚡", title: "Instant access", body: "Financing links ready when you are." },
              { icon: "🛡", title: "Secure & trusted", body: "Enterprise-grade application flow." },
              { icon: "🔗", title: "Seamlessly connected", body: "Channels connect to one RoofHub view." },
            ].map((item) => (
              <Flex key={item.title} sx={{ gap: 3, alignItems: "flex-start" }}>
                <Text sx={{ fontSize: "1.5rem", lineHeight: 1 }}>{item.icon}</Text>
                <Box>
                  <Text sx={{ fontWeight: 700, display: "block", mb: 1, color: text.primary }}>{item.title}</Text>
                  <Text sx={{ fontSize: "0.9rem", color: text.muted }}>{item.body}</Text>
                </Box>
              </Flex>
            ))}
          </Grid>
        </Grid>
      </Box>

      {/* How it works */}
      <Box sx={{ backgroundColor: gl.washMuted, py: [3, 4], borderTop: `1px solid ${border.tealSoft}` }}>
        <Box sx={section}>
          <Box
            as="h2"
            sx={{
              textAlign: "center",
              fontSize: ["1.45rem", "1.65rem"],
              fontWeight: 800,
              mb: 3,
              color: gl.teal,
              lineHeight: 1.2,
            }}
          >
            How it works
          </Box>

          <Grid columns={[1, 1, 3]} gap={3}>
            <StepCard
              step={1}
              title="Share GoodLeap Plus on any channel"
              body="Add your financing link to your website, SMS campaigns, or email templates. One consistent experience everywhere."
              channels={["🌐 Website", "💬 SMS", "✉️ Email", "🔗 Links"]}
            />
            <StepCard
              step={2}
              title="Customers apply in minutes"
              body="Homeowners tap your GoodLeap Plus call-to-action, complete a short application, and move forward without friction."
            />
            <StepCard
              step={3}
              title="Applications flow into RoofHub"
              body="Submissions and statuses land in RoofHub so your team tracks applications, approvals, and next steps in one place."
            />
          </Grid>
        </Box>
      </Box>

      {/* Infographic */}
      {/* <Box sx={{ ...section, maxWidth: "960px", my: [3, 4], textAlign: "center" }}>
        <img src={roofHubInfographic} alt="Infographic" />
      </Box> */}

      {/* RoofHub at the core — hub-and-spoke diagram (reference layout; brand = maroon + teal) */}
      <Box
        sx={{
          pt: [3, 4],
          pb: [4, 5],
          borderTop: `1px solid ${border.tealSoft}`,
          background: `linear-gradient(180deg, ${gl.washMuted} 0%, #fff 38%)`,
        }}
      >
        <Box sx={{ ...section, maxWidth: "960px" }}>
          <Box as="h2" sx={{ textAlign: "center", mb: [3, 4], lineHeight: 1.35 }}>
            <Text
              as="span"
              sx={{
                display: "block",
                fontSize: ["1rem", "1.2rem"],
                fontWeight: 900,
                color: rh.maroon,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              RoofHub™ at the core.
            </Text>
            <Text
              as="span"
              sx={{
                display: "block",
                mt: [2, "10px"],
                fontSize: ["0.82rem", "0.95rem"],
                fontWeight: 800,
                color: gl.teal,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              GoodLeap Plus connects every channel.
            </Text>
          </Box>

          {/* Mobile / narrow: stacked hub + 2×2 channels */}
          <Flex sx={{ display: ["flex", "none"], flexDirection: "column", alignItems: "center", gap: 3 }}>
            <HubFlowCaption direction="in" />
            <HubCircleGraphic />
            <HubFlowCaption direction="out" />
            <Box sx={{ width: "100%", display: ["block", "none"], mt: 2 }}>
              <GoodLeapPlusPartnerFlowStrip />
            </Box>
            <Grid columns={2} gap={2} sx={{ width: "100%", mt: 1 }}>
              <HubChannelCard
                glyph="🌐"
                title="WEB (Your Website)"
                description="Embed GoodLeap Plus on your website so customers can apply with one click."
                mockup={<HubMockupWeb />}
              />
              <HubChannelCard
                glyph="✉️"
                title="EMAIL"
                description="Include GoodLeap Plus in your emails to drive easy financing access."
                mockup={<HubMockupEmail />}
              />
              <HubChannelCard
                glyph="💬"
                title="SMS (Text)"
                description="Send GoodLeap Plus via text so customers can apply anytime, anywhere."
                mockup={<HubMockupSms />}
              />
              <HubChannelCard
                glyph="🔗"
                title="LINKS (Shareable)"
                description="Share your GoodLeap Plus link through any channel. One link does it all."
                mockup={<HubMockupLinks />}
              />
            </Grid>
          </Flex>

          {/* Desktop: hub-and-spoke grid + dashed connectors */}
          <Box
            sx={{
              display: ["none", "grid"],
              position: "relative",
              gridTemplateColumns: "minmax(0, 1fr) minmax(216px, 272px) minmax(0, 1fr)",
              gridTemplateRows: "auto auto auto auto auto",
              alignItems: "center",
              justifyItems: "center",
              columnGap: [2, 3],
              rowGap: [2, 3],
              minHeight: ["auto", "440px", "480px"],
              mt: 1,
            }}
          >
            <HubSpokeLines />
            <Box sx={{ gridColumn: 2, gridRow: 1, zIndex: 1 }}>
              <HubFlowCaption direction="in" />
            </Box>
            <Box sx={{ gridColumn: 1, gridRow: 2, justifySelf: "end", width: "100%", maxWidth: "300px", zIndex: 1 }}>
              <HubChannelCard
                glyph="🌐"
                title="WEB (Your Website)"
                description="Embed GoodLeap Plus on your website so customers can apply with one click."
                mockup={<HubMockupWeb />}
              />
            </Box>
            <Box
              sx={{
                gridColumn: 2,
                gridRow: "2 / 5",
                alignSelf: "center",
                zIndex: 1,
              }}
            >
              <HubCircleGraphic />
            </Box>
            <Box sx={{ gridColumn: 3, gridRow: 2, justifySelf: "start", width: "100%", maxWidth: "300px", zIndex: 1 }}>
              <HubChannelCard
                glyph="✉️"
                title="EMAIL"
                description="Include GoodLeap Plus in your emails to drive easy financing access."
                mockup={<HubMockupEmail />}
              />
            </Box>
            <Box sx={{ gridColumn: 1, gridRow: 4, justifySelf: "end", width: "100%", maxWidth: "300px", zIndex: 1 }}>
              <HubChannelCard
                glyph="💬"
                title="SMS (Text)"
                description="Send GoodLeap Plus via text so customers can apply anytime, anywhere."
                mockup={<HubMockupSms />}
              />
            </Box>
            <Box sx={{ gridColumn: 3, gridRow: 4, justifySelf: "start", width: "100%", maxWidth: "300px", zIndex: 1 }}>
              <HubChannelCard
                glyph="🔗"
                title="LINKS (Shareable)"
                description="Share your GoodLeap Plus link through any channel. One link does it all."
                mockup={<HubMockupLinks />}
              />
            </Box>
            <Box sx={{ gridColumn: 2, gridRow: 5, zIndex: 1 }}>
              <HubFlowCaption direction="out" />
            </Box>
          </Box>
          <Box sx={{ width: "100%", display: ["none", "block"], mt: 3 }}>
            <GoodLeapPlusPartnerFlowStrip />
          </Box>
        </Box>
      </Box>

      {/* Benefits strip — GoodLeap deep teal (one row: title line + body line per column) */}
      <Box sx={{ backgroundColor: gl.teal, color: "#fff", py: [3, 4] }}>
        <Box sx={{ ...section, overflowX: ["auto", "visible"], WebkitOverflowScrolling: "touch" }}>
          <Flex
            sx={{
              flexWrap: "nowrap",
              gap: [2, 3],
              alignItems: "flex-start",
              minWidth: ["720px", "100%"],
            }}
          >
            {[
              { title: "Email signature", body: "Drop a financing link into every outbound message." },
              { title: "Integrated experience", body: "From your site to RoofHub without duplicate entry." },
              { title: "Faster", body: "Instant links—no waiting on one-off PDFs or portals." },
              { title: "Smarter", body: "Structured data flows straight into RoofHub." },
              { title: "Stronger together", body: "SRS, RoofHub, and GoodLeap aligned for contractors." },
            ].map((b, i) => (
              <Box
                key={b.title}
                sx={{
                  flex: "1 1 0",
                  minWidth: 0,
                  borderLeft: i === 0 ? "none" : `3px solid ${rh.gold}`,
                  pl: i === 0 ? 0 : 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <Text
                  sx={{
                    fontWeight: 700,
                    fontSize: ["0.82rem", "0.9rem"],
                    mb: 2,
                    color: "#fff",
                    letterSpacing: "0.04em",
                    lineHeight: 1.25,
                    whiteSpace: "normal",
                    hyphens: "auto",
                  }}
                >
                  {b.title}
                </Text>
                <Text
                  sx={{
                    fontSize: "0.85rem",
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,0.92)",
                  }}
                >
                  {b.body}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      </Box>

      {/* Footer tagline — RoofHub maroon */}
      <Box
        sx={{
          backgroundColor: rh.maroon,
          color: "#fff",
          py: 3,
          textAlign: "center",
          px: 3,
          borderTop: `4px solid ${gl.orange}`,
        }}
      >
        <Box sx={{ maxWidth: "720px", mx: "auto" }}>
          <Text
            as="p"
            sx={{
              m: 0,
              fontSize: ["1.15rem", "1.35rem", "1.5rem"],
              fontWeight: 800,
              lineHeight: 1.25,
              color: "#fff",
              whiteSpace: ["normal", "nowrap"],
              textAlign: "center",
            }}
          >
            One link. More approvals. Stronger together.
          </Text>
          <Text
            as="p"
            sx={{
              m: 0,
              mt: [2, 2.5],
              fontSize: ["0.9rem", "0.95rem"],
              fontWeight: 400,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.78)",
              textAlign: "center",
            }}
          >
            GoodLeap Plus makes financing simple for your customers and your business.
          </Text>
        </Box>
      </Box>

      <Box sx={{ backgroundColor: rh.black, py: 3, px: 2 }}>
        <Grid
          columns={[1, 3]}
          gap={[3, 4]}
          sx={{
            maxWidth: "720px",
            mx: "auto",
            width: "100%",
            alignItems: "center",
            justifyItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: FOOTER_CELL_MIN_H,
              minWidth: 0,
              px: 2,
            }}
          >
            <Box sx={FOOTER_LOGO_BOX}>
              <img
                src={srsLogoWhite}
                alt="SRS Distribution"
                style={{ ...logoImgFooterLockup, filter: "brightness(0) invert(1)" }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: FOOTER_CELL_MIN_H,
              minWidth: 0,
              px: 2,
            }}
          >
            <Box sx={FOOTER_LOGO_BOX}>
              <img src={roofHubLogoWhite} alt="RoofHub" style={logoImgFooterLockup} />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: FOOTER_CELL_MIN_H,
              minWidth: 0,
              px: 2,
            }}
          >
            <Box sx={FOOTER_LOGO_BOX}>
              <img src={goodLeapLogoWhite} alt="Powered by GoodLeap" style={logoImgFooterLockup} />
            </Box>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

/** White / light-only marks need a dark plate on light UI */
function TealBrandPlate({ children, sx: sxExtra = {}, tone = "teal" }) {
  const bg = tone === "maroon" ? rh.maroon : gl.teal;
  return (
    <Flex
      sx={{
        backgroundColor: bg,
        px: 3,
        py: 2,
        borderRadius: "8px",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 0,
        ...sxExtra,
      }}
    >
      {children}
    </Flex>
  );
}

function HubFlowCaption({ direction }) {
  const dottedLine = (
    <Box
      sx={{
        width: "1px",
        height: ["28px", "36px"],
        borderLeft: `2px dotted ${gl.tealMid}`,
      }}
    />
  );
  if (direction === "in") {
    return (
      <Flex sx={{ flexDirection: "column", alignItems: "center", textAlign: "center" }}>
        <Text
          sx={{
            fontSize: ["0.74rem", "0.8rem"],
            fontWeight: 800,
            letterSpacing: "0.14em",
            color: gl.teal,
            textTransform: "uppercase",
            maxWidth: "220px",
            lineHeight: 1.35,
          }}
        >
          Applications flow in
        </Text>
        <Box sx={{ mt: 1 }}>{dottedLine}</Box>
        <Text sx={{ fontSize: "0.75rem", color: gl.teal, lineHeight: 1, mt: "10px" }}>▼</Text>
      </Flex>
    );
  }
  return (
    <Flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        mt: ["-10px", "-14px"],
      }}
    >
      <Text sx={{ fontSize: "0.75rem", color: gl.teal, lineHeight: 1, mb: "8px" }}>▼</Text>
      <Box sx={{ mb: "8px" }}>{dottedLine}</Box>
      <Text
        sx={{
          fontSize: ["0.74rem", "0.8rem"],
          fontWeight: 800,
          letterSpacing: "0.12em",
          color: gl.teal,
          textTransform: "uppercase",
          maxWidth: "260px",
          lineHeight: 1.35,
        }}
      >
        Approvals &amp; status flow out
      </Text>
    </Flex>
  );
}

function HubCircleGraphic() {
  return (
    <Flex
      sx={{
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: ["228px", "252px", "276px"],
        height: ["228px", "252px", "276px"],
        borderRadius: "50%",
        border: `3px solid ${rh.maroon}`,
        backgroundColor: "#fff",
        boxShadow: "0 10px 36px rgba(118, 33, 35, 0.14)",
        px: [3, 3],
        py: 3,
        textAlign: "center",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          height: [76, 88, 96],
          width: "92%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <img
          src={roofHubLogo}
          alt="RoofHub"
          style={{
            maxHeight: "100%",
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
      <Box sx={{ width: 52, height: 2, backgroundColor: rh.gold, my: 2, borderRadius: 1 }} />
      <Text sx={{ fontSize: ["0.68rem", "0.72rem"], lineHeight: 1.5, color: text.muted }}>
        All applications and approvals flow into RoofHub. One system. Complete visibility.
      </Text>
    </Flex>
  );
}

function HubSpokeLines() {
  const stroke = gl.tealMid;
  return (
    <Box
      as="svg"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        opacity: 0.55,
      }}
    >
      <line
        x1="50"
        y1="50"
        x2="14"
        y2="26"
        stroke={stroke}
        strokeWidth="0.4"
        strokeDasharray="2 3"
      />
      <line
        x1="50"
        y1="50"
        x2="86"
        y2="26"
        stroke={stroke}
        strokeWidth="0.4"
        strokeDasharray="2 3"
      />
      <line
        x1="50"
        y1="50"
        x2="14"
        y2="74"
        stroke={stroke}
        strokeWidth="0.4"
        strokeDasharray="2 3"
      />
      <line
        x1="50"
        y1="50"
        x2="86"
        y2="74"
        stroke={stroke}
        strokeWidth="0.4"
        strokeDasharray="2 3"
      />
    </Box>
  );
}

function HubChannelCard({ glyph, title, description, mockup }) {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid rgba(0, 52, 57, 0.18)`,
        borderRadius: "12px",
        p: [2, 3],
        boxShadow: "0 4px 22px rgba(0, 52, 57, 0.07)",
        width: "100%",
      }}
    >
      <Flex sx={{ gap: 2, alignItems: "flex-start", mb: 2 }}>
        <Flex
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            backgroundColor: gl.teal,
            color: "#fff",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.15rem",
            flexShrink: 0,
          }}
        >
          {glyph}
        </Flex>
        <Box sx={{ minWidth: 0, pt: "4px" }}>
          <Text
            sx={{
              fontWeight: 800,
              fontSize: "0.78rem",
              color: gl.teal,
              letterSpacing: "0.04em",
              lineHeight: 1.35,
            }}
          >
            {title}
          </Text>
        </Box>
      </Flex>
      <Text sx={{ fontSize: "0.8rem", color: text.muted, lineHeight: 1.55 }}>{description}</Text>
      {mockup}
    </Box>
  );
}

function HubMockupWeb() {
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#fff",
        border: `1px solid ${border.tealSoft}`,
        borderRadius: "8px",
      }}
    >
      <Text sx={{ fontWeight: 700, fontSize: "0.68rem", color: text.primary }}>Your Roofing Company</Text>
      <Text sx={{ fontSize: "1.2rem", my: 1, lineHeight: 1 }} aria-hidden>
        🏠
      </Text>
      <Box
        sx={{
          mt: 2,
          backgroundColor: gl.teal,
          color: "#fff",
          fontSize: "0.62rem",
          fontWeight: 800,
          py: "10px",
          px: 2,
          borderRadius: "6px",
          textAlign: "center",
          lineHeight: 1.35,
        }}
      >
        Apply with GoodLeap Plus Financing
      </Box>
    </Box>
  );
}

function HubMockupSms() {
  return (
    <Box sx={{ mt: 2, backgroundColor: "#e8eaed", p: 2, borderRadius: "14px", borderTopLeftRadius: "4px" }}>
      <Text sx={{ fontSize: "0.68rem", color: text.primary, lineHeight: 1.45 }}>
        Hi John, your roofing project financing is ready!
      </Text>
      <Box
        sx={{
          mt: 2,
          backgroundColor: "#fff",
          border: `2px solid ${gl.tealBright}`,
          borderRadius: "6px",
          py: "8px",
          px: 2,
          fontSize: "0.6rem",
          fontWeight: 800,
          color: gl.teal,
          textAlign: "center",
          lineHeight: 1.35,
        }}
      >
        Apply with GoodLeap Plus Financing
      </Box>
      <Text sx={{ fontSize: "0.62rem", color: gl.teal, mt: 1, fontWeight: 600 }}>goodleap.plus/6b5889ce-45ef-453a-b0cc-8f1c2df264fb</Text>
    </Box>
  );
}

function HubMockupEmail() {
  return (
    <Box sx={{ mt: 2, p: 2, border: `1px solid ${border.tealSoft}`, borderRadius: "8px", backgroundColor: "#fff" }}>
      <Text sx={{ fontSize: "0.68rem", lineHeight: 1.5, color: text.primary }}>
        Hello John, Complete your project stress-free. Financing is one click away.
      </Text>
      <Box
        sx={{
          mt: 2,
          border: `2px solid ${gl.tealBright}`,
          borderRadius: "6px",
          py: "8px",
          px: 2,
          fontSize: "0.6rem",
          fontWeight: 800,
          color: gl.teal,
          textAlign: "center",
          lineHeight: 1.35,
        }}
      >
        Apply with GoodLeap Plus Financing
      </Box>
      <Text sx={{ fontSize: "0.62rem", color: text.subtle, mt: 1 }}>Fast. Simple. Secure.</Text>
    </Box>
  );
}

function HubMockupLinks() {
  return (
    <Box>
      <Box
        sx={{
          mt: 2,
          p: 2,
          border: `2px solid ${gl.tealBright}`,
          borderRadius: "8px",
          textAlign: "center",
          backgroundColor: "#fff",
        }}
      >
        <Text sx={{ fontSize: "0.68rem", fontWeight: 800, color: gl.teal }}>GoodLeap Plus Financing Link</Text>
      </Box>
      <Text sx={{ fontSize: "0.62rem", color: gl.teal, mt: 1, fontWeight: 600 }}>goodleap.plus/6b5889ce-45ef-453a-b0cc-8f1c2df264fb</Text>
    </Box>
  );
}

function StepCard({ step, title, body, channels }) {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: `1px solid ${border.tealSoft}`,
        borderTop: `3px solid ${gl.tealBright}`,
        p: 3,
        boxShadow: "0 8px 24px rgba(0, 52, 57, 0.07)",
      }}
    >
      <Text sx={{ fontSize: "0.75rem", letterSpacing: "0.12em", color: gl.tealBright, fontWeight: 700 }}>
        STEP {step}
      </Text>
      <Box as="h3" sx={{ fontSize: "1.1rem", fontWeight: 800, mt: 2, mb: 2, color: rh.maroon, lineHeight: 1.3 }}>
        {title}
      </Box>
      <Text sx={{ fontSize: "0.95rem", color: text.muted, lineHeight: 1.6 }}>{body}</Text>
      {channels && (
        <Flex sx={{ flexWrap: "wrap", gap: 2, mt: 3 }}>
          {channels.map((c) => (
            <Text
              key={c}
              sx={{
                fontSize: "0.8rem",
                backgroundColor: gl.wash,
                color: gl.teal,
                px: 2,
                py: 1,
                borderRadius: "4px",
                border: `1px solid ${border.tealSoft}`,
              }}
            >
              {c}
            </Text>
          ))}
        </Flex>
      )}
    </Box>
  );
}

export default RoofHub;
